import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { getISODateInBarbados } from '../../../utils/timezone';
import { accountService } from '../../../services/accountService';
import { categoryService } from '../../../services/categoryService';

const TransferModal = ({ onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    date: getISODateInBarbados(),
    fromAccountId: '',
    toAccountId: '',
    amount: '',
    description: '',
    notes: ''
  });
  const [accounts, setAccounts] = useState([]);
  const [transferInCategoryId, setTransferInCategoryId] = useState(null);
  const [transferOutCategoryId, setTransferOutCategoryId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [accountsData, categoriesData] = await Promise.all([
        accountService?.getAll(),
        categoryService?.getAll()
      ]);
      
      setAccounts(accountsData || []);
      
      // Find Transfer In subcategory (under income)
      const transferInCategory = categoriesData?.find(cat => 
        cat?.name === 'Transfer In' && cat?.type === 'income' && cat?.parentId
      );
      setTransferInCategoryId(transferInCategory?.id || null);
      
      // Find Transfer Out subcategory (under expense)
      const transferOutCategory = categoriesData?.find(cat => 
        cat?.name === 'Transfer Out' && cat?.type === 'expense' && cat?.parentId
      );
      setTransferOutCategoryId(transferOutCategory?.id || null);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const accountOptions = accounts?.map(acc => ({
    value: acc?.id,
    label: acc?.name
  }));

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (formData?.fromAccountId === formData?.toAccountId) {
      alert('Source and destination accounts must be different');
      return;
    }
    
    const fromAccount = accounts?.find(acc => acc?.id === formData?.fromAccountId);
    const toAccount = accounts?.find(acc => acc?.id === formData?.toAccountId);
    
    // Determine transfer type based on From Account group
    // Assets/Cash accounts use Transfer Out (expense)
    // Liabilities accounts use Transfer In (income)
    const isFromAccountAsset = fromAccount?.group === 'Assets' || fromAccount?.group === 'Cash';
    const useTransferOut = isFromAccountAsset;
    
    // For outgoing transaction (from account)
    const outgoingCategoryId = useTransferOut ? transferOutCategoryId : transferInCategoryId;
    const outgoingType = useTransferOut ? 'expense' : 'income';
    
    // For incoming transaction (to account)
    const incomingCategoryId = useTransferOut ? transferInCategoryId : transferOutCategoryId;
    const incomingType = useTransferOut ? 'income' : 'expense';
    
    onSubmit({
      date: new Date(formData?.date)?.toISOString(),
      fromAccountId: formData?.fromAccountId,
      toAccountId: formData?.toAccountId,
      fromAccount: fromAccount?.name || '',
      toAccount: toAccount?.name || '',
      outgoingCategoryId,
      outgoingType,
      incomingCategoryId,
      incomingType,
      amount: parseFloat(formData?.amount),
      description: formData?.description || `Transfer from ${fromAccount?.name} to ${toAccount?.name}`,
      notes: formData?.notes || null
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const availableToAccounts = accountOptions?.filter(
    opt => opt?.value !== formData?.fromAccountId
  );
  
  // Get transfer type label based on selected from account
  const getTransferTypeLabel = () => {
    if (!formData?.fromAccountId) return 'Transfer';
    const fromAccount = accounts?.find(acc => acc?.id === formData?.fromAccountId);
    if (!fromAccount) return 'Transfer';
    
    const isAsset = fromAccount?.group === 'Assets' || fromAccount?.group === 'Cash';
    return isAsset ? 'Transfer Out' : 'Transfer In';
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-4 w-full max-w-[600px] mx-16 max-h-[90vh] overflow-y-auto">
        <div className="p-20 md:p-24 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-12">
            <Icon name="ArrowLeftRight" size={24} color="var(--color-primary)" />
            <div>
              <h2 className="text-xl font-heading font-semibold text-card-foreground">
                Create Transfer
              </h2>
              <p className="text-sm text-muted-foreground mt-4">
                Move funds between accounts
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            onClick={onClose}
          />
        </div>

        <form onSubmit={handleSubmit} className="p-20 md:p-24">
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <div className="text-muted-foreground">Loading accounts...</div>
            </div>
          ) : (
            <div className="space-y-16">
              <div>
                <label className="block text-sm font-caption font-medium text-foreground mb-8">
                  Date *
                </label>
                <Input
                  type="date"
                  value={formData?.date}
                  onChange={(e) => handleChange('date', e?.target?.value)}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                <div>
                  <label className="block text-sm font-caption font-medium text-foreground mb-8">
                    From Account *
                  </label>
                  <Select
                    options={accountOptions}
                    value={formData?.fromAccountId}
                    onChange={(value) => handleChange('fromAccountId', value)}
                    placeholder="Select source account"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-caption font-medium text-foreground mb-8">
                    To Account *
                  </label>
                  <Select
                    options={availableToAccounts}
                    value={formData?.toAccountId}
                    onChange={(value) => handleChange('toAccountId', value)}
                    placeholder="Select destination account"
                    disabled={!formData?.fromAccountId}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-caption font-medium text-foreground mb-8">
                  Amount *
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData?.amount}
                  onChange={(e) => handleChange('amount', e?.target?.value)}
                  placeholder="0.00"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-caption font-medium text-foreground mb-8">
                  Description (Optional)
                </label>
                <Input
                  type="text"
                  value={formData?.description}
                  onChange={(e) => handleChange('description', e?.target?.value)}
                  placeholder="Enter transfer description"
                />
              </div>

              <div>
                <label className="block text-sm font-caption font-medium text-foreground mb-8">
                  Notes (Optional)
                </label>
                <Input
                  type="text"
                  value={formData?.notes}
                  onChange={(e) => handleChange('notes', e?.target?.value)}
                  placeholder="Add notes for both transactions"
                />
              </div>

              <div className="bg-muted rounded-lg p-16">
                <div className="flex items-start gap-12">
                  <Icon name="Info" size={16} className="text-primary mt-2" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-4">Transfer Details ({getTransferTypeLabel()}):</p>
                    <ul className="space-y-2 list-disc list-inside">
                      <li>Two linked transactions will be created</li>
                      <li>Debit from source account</li>
                      <li>Credit to destination account</li>
                      <li>Categorized as {getTransferTypeLabel()} based on account type</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-12 mt-24">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={loading}
            >
              Create Transfer
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransferModal;